import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import webhookService from "../../../lib/webhookService";
import { useAuth } from "../../../contexts/AuthContext";

const WebhookLogsModal = ({ isOpen, onClose, webhook }) => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    event: "all",
    timeRange: "24h",
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 10;

  // Mock webhook delivery logs
  const mockLogs = [
    {
      id: "log_001",
      timestamp: "2024-01-20T14:35:23Z",
      event: "message.sent",
      status: "success",
      statusCode: 200,
      responseTime: 145,
      attempts: 1,
      payload: {
        event: "message.sent",
        data: {
          message_id: "msg_001",
          recipient: "+1234567890",
          content: "Hello World!",
        },
        timestamp: "2024-01-20T14:35:23Z",
      },
      response: { status: "received", id: "webhook_001" },
      error: null,
    },
    {
      id: "log_002",
      timestamp: "2024-01-20T14:32:15Z",
      event: "message.delivered",
      status: "success",
      statusCode: 201,
      responseTime: 234,
      attempts: 1,
      payload: {
        event: "message.delivered",
        data: {
          message_id: "msg_002",
          recipient: "+1234567891",
          delivered_at: "2024-01-20T14:32:15Z",
        },
        timestamp: "2024-01-20T14:32:15Z",
      },
      response: { message: "Processed successfully" },
      error: null,
    },
    {
      id: "log_003",
      timestamp: "2024-01-20T14:28:45Z",
      event: "user.joined",
      status: "failed",
      statusCode: 404,
      responseTime: 120,
      attempts: 3,
      payload: {
        event: "user.joined",
        data: {
          user_id: "user_123",
          channel_id: "ch_456",
          joined_at: "2024-01-20T14:28:45Z",
        },
        timestamp: "2024-01-20T14:28:45Z",
      },
      response: null,
      error: "Endpoint not found",
    },
    {
      id: "log_004",
      timestamp: "2024-01-20T14:25:12Z",
      event: "message.failed",
      status: "failed",
      statusCode: 500,
      responseTime: 5000,
      attempts: 3,
      payload: {
        event: "message.failed",
        data: {
          message_id: "msg_003",
          error: "Invalid recipient",
          failed_at: "2024-01-20T14:25:12Z",
        },
        timestamp: "2024-01-20T14:25:12Z",
      },
      response: null,
      error: "Internal Server Error",
    },
    {
      id: "log_005",
      timestamp: "2024-01-20T14:22:33Z",
      event: "message.read",
      status: "success",
      statusCode: 200,
      responseTime: 89,
      attempts: 1,
      payload: {
        event: "message.read",
        data: {
          message_id: "msg_004",
          recipient: "+1234567892",
          read_at: "2024-01-20T14:22:33Z",
        },
        timestamp: "2024-01-20T14:22:33Z",
      },
      response: { acknowledged: true },
      error: null,
    },
    {
      id: "log_006",
      timestamp: "2024-01-20T14:18:56Z",
      event: "user.left",
      status: "retry",
      statusCode: 0,
      responseTime: 10000,
      attempts: 2,
      payload: {
        event: "user.left",
        data: {
          user_id: "user_456",
          channel_id: "ch_789",
          left_at: "2024-01-20T14:18:56Z",
        },
        timestamp: "2024-01-20T14:18:56Z",
      },
      response: null,
      error: "Connection timeout",
    },
    {
      id: "log_007",
      timestamp: "2024-01-20T14:15:41Z",
      event: "channel.created",
      status: "success",
      statusCode: 202,
      responseTime: 178,
      attempts: 1,
      payload: {
        event: "channel.created",
        data: {
          channel_id: "ch_new",
          name: "new-channel",
          created_by: "user_789",
        },
        timestamp: "2024-01-20T14:15:41Z",
      },
      response: { queued: true, process_id: "proc_123" },
      error: null,
    },
    {
      id: "log_008",
      timestamp: "2024-01-20T14:12:27Z",
      event: "message.sent",
      status: "failed",
      statusCode: 429,
      responseTime: 95,
      attempts: 1,
      payload: {
        event: "message.sent",
        data: {
          message_id: "msg_005",
          recipient: "+1234567893",
          content: "Rate limited message",
        },
        timestamp: "2024-01-20T14:12:27Z",
      },
      response: { error: "Rate limit exceeded" },
      error: "Too Many Requests",
    },
    {
      id: "log_009",
      timestamp: "2024-01-20T14:09:18Z",
      event: "integration.connected",
      status: "success",
      statusCode: 200,
      responseTime: 201,
      attempts: 1,
      payload: {
        event: "integration.connected",
        data: {
          integration_id: "int_456",
          type: "api",
          connected_at: "2024-01-20T14:09:18Z",
        },
        timestamp: "2024-01-20T14:09:18Z",
      },
      response: { status: "integration_recorded" },
      error: null,
    },
    {
      id: "log_010",
      timestamp: "2024-01-20T14:06:52Z",
      event: "user.activity",
      status: "success",
      statusCode: 204,
      responseTime: 67,
      attempts: 1,
      payload: {
        event: "user.activity",
        data: { user_id: "user_321", activity: "typing", channel_id: "ch_654" },
        timestamp: "2024-01-20T14:06:52Z",
      },
      response: null,
      error: null,
    },
  ];

  useEffect(() => {
    if (isOpen && webhook && user?.id) {
      loadLogs();
    }
  }, [isOpen, webhook, user?.id, filters]);

  const loadLogs = async () => {
    if (!user?.id || !webhook?.id) return;

    setLoading(true);
    try {
      const { data, error } = await webhookService.getWebhookLogs(
        webhook.id,
        user.id,
        filters,
      );

      if (error) {
        console.error("Failed to load webhook logs:", error);
        setLogs([]);
      } else {
        setLogs(data || []);
      }
    } catch (error) {
      console.error("Failed to load webhook logs:", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "bg-accent/10 text-accent border-accent/20";
      case "failed":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "retry":
        return "bg-warning/10 text-warning border-warning/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return "CheckCircle";
      case "failed":
        return "XCircle";
      case "retry":
        return "RotateCcw";
      default:
        return "Clock";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatResponseTime = (ms) => {
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${ms}ms`;
  };

  const getCurrentPageLogs = () => {
    const startIndex = (currentPage - 1) * logsPerPage;
    const endIndex = startIndex + logsPerPage;
    return logs.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(logs.length / logsPerPage);
  };

  const getUniqueEvents = () => {
    return [...new Set(logs.map((log) => log.event_type || log.event))];
  };

  const retryDelivery = async (logId) => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const result = await webhookService.retryWebhookDelivery(logId, user.id);

      if (result.success) {
        // Reload logs to get updated data
        await loadLogs();
      } else {
        console.error("Failed to retry delivery:", result.error);
      }
    } catch (error) {
      console.error("Failed to retry delivery:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !webhook) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Webhook Delivery Logs
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Delivery history for:{" "}
                <code className="bg-muted px-1 rounded">{webhook.name}</code>
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-border bg-muted/30">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-foreground">
                  Status:
                </label>
                <select
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                  className="px-3 py-1 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="all">All Statuses</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="retry">Retrying</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-foreground">
                  Event:
                </label>
                <select
                  value={filters.event}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, event: e.target.value }))
                  }
                  className="px-3 py-1 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="all">All Events</option>
                  {getUniqueEvents().map((event) => (
                    <option key={event} value={event}>
                      {event}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-foreground">
                  Time Range:
                </label>
                <select
                  value={filters.timeRange}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      timeRange: e.target.value,
                    }))
                  }
                  className="px-3 py-1 border border-border rounded bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="1h">Last Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>

              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={loadLogs}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {selectedLog ? (
              /* Log Detail View */
              <div className="h-full flex flex-col">
                <div className="p-6 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSelectedLog(null)}
                      >
                        <Icon name="ArrowLeft" size={16} />
                      </Button>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">
                          Delivery Details
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Log ID: {selectedLog.id}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedLog.status)}`}
                    >
                      <Icon
                        name={getStatusIcon(selectedLog.status)}
                        size={14}
                        className="inline mr-1"
                      />
                      {selectedLog.status}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">
                        Event Type
                      </span>
                      <p className="font-medium text-foreground">
                        {selectedLog.event_type || selectedLog.event}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">
                        Status Code
                      </span>
                      <p className="font-medium text-foreground">
                        {selectedLog.status_code ||
                          selectedLog.statusCode ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">
                        Response Time
                      </span>
                      <p className="font-medium text-foreground">
                        {formatResponseTime(
                          selectedLog.response_time || selectedLog.responseTime,
                        )}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">
                        Attempts
                      </span>
                      <p className="font-medium text-foreground">
                        {selectedLog.attempts}
                      </p>
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div>
                    <span className="text-sm text-muted-foreground">
                      Timestamp
                    </span>
                    <p className="font-medium text-foreground">
                      {formatDate(
                        selectedLog.created_at || selectedLog.timestamp,
                      )}
                    </p>
                  </div>

                  {/* Error */}
                  {(selectedLog.error_message || selectedLog.error) && (
                    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                      <h4 className="font-medium text-destructive mb-2">
                        Error Details
                      </h4>
                      <p className="text-sm text-destructive">
                        {selectedLog.error_message || selectedLog.error}
                      </p>
                    </div>
                  )}

                  {/* Payload */}
                  <div>
                    <h4 className="font-medium text-foreground mb-2">
                      Request Payload
                    </h4>
                    <pre className="bg-muted p-4 rounded-lg text-xs text-foreground overflow-auto max-h-60 border border-border">
                      {JSON.stringify(selectedLog.payload, null, 2)}
                    </pre>
                  </div>

                  {/* Response */}
                  {(selectedLog.response_data || selectedLog.response) && (
                    <div>
                      <h4 className="font-medium text-foreground mb-2">
                        Response
                      </h4>
                      <pre className="bg-muted p-4 rounded-lg text-xs text-foreground overflow-auto max-h-60 border border-border">
                        {JSON.stringify(
                          selectedLog.response_data || selectedLog.response,
                          null,
                          2,
                        )}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Logs List View */
              <div className="h-full flex flex-col">
                {loading ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-center">
                    <div>
                      <Icon
                        name="FileText"
                        size={48}
                        className="text-muted-foreground mx-auto mb-4"
                      />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        No logs found
                      </h3>
                      <p className="text-muted-foreground">
                        No webhook delivery logs match your current filters.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Logs Table */}
                    <div className="flex-1 overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-muted border-b border-border">
                          <tr>
                            <th className="text-left p-4 text-sm font-medium text-foreground">
                              Timestamp
                            </th>
                            <th className="text-left p-4 text-sm font-medium text-foreground">
                              Event
                            </th>
                            <th className="text-left p-4 text-sm font-medium text-foreground">
                              Status
                            </th>
                            <th className="text-left p-4 text-sm font-medium text-foreground">
                              Response Time
                            </th>
                            <th className="text-left p-4 text-sm font-medium text-foreground">
                              Attempts
                            </th>
                            <th className="text-left p-4 text-sm font-medium text-foreground">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {getCurrentPageLogs().map((log) => (
                            <tr
                              key={log.id}
                              className="hover:bg-muted/50 cursor-pointer"
                              onClick={() => setSelectedLog(log)}
                            >
                              <td className="p-4 text-sm text-foreground">
                                {formatDate(log.created_at || log.timestamp)}
                              </td>
                              <td className="p-4">
                                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded border border-primary/20">
                                  {log.event_type || log.event}
                                </span>
                              </td>
                              <td className="p-4">
                                <div
                                  className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(log.status)}`}
                                >
                                  <Icon
                                    name={getStatusIcon(log.status)}
                                    size={12}
                                  />
                                  <span>{log.status}</span>
                                </div>
                              </td>
                              <td className="p-4 text-sm text-foreground">
                                {formatResponseTime(
                                  log.response_time || log.responseTime,
                                )}
                              </td>
                              <td className="p-4 text-sm text-foreground">
                                {log.attempts}
                              </td>
                              <td className="p-4">
                                <div className="flex items-center space-x-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedLog(log);
                                    }}
                                    title="View Details"
                                  >
                                    <Icon name="Eye" size={12} />
                                  </Button>
                                  {log.status === "failed" && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        retryDelivery(log.id);
                                      }}
                                      title="Retry Delivery"
                                      disabled={loading}
                                    >
                                      <Icon name="RotateCcw" size={12} />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {getTotalPages() > 1 && (
                      <div className="p-6 border-t border-border">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground">
                            Showing {(currentPage - 1) * logsPerPage + 1} to{" "}
                            {Math.min(currentPage * logsPerPage, logs.length)}{" "}
                            of {logs.length} results
                          </p>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="ChevronLeft"
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(prev - 1, 1))
                              }
                              disabled={currentPage === 1}
                            />
                            <span className="text-sm text-foreground">
                              Page {currentPage} of {getTotalPages()}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="ChevronRight"
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(prev + 1, getTotalPages()),
                                )
                              }
                              disabled={currentPage === getTotalPages()}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebhookLogsModal;
